<template>
  <div class="nav-mobile-container">
    <input
      id="navMobileToggle"
      v-model="isNavMobileOpen"
      type="checkbox"
    />

    <label for="navMobileToggle">
      <NavToggle open />
    </label>

    <div class="nav-mobile-wrapper">
      <nav
        class="nav-mobile"
        :class="[
          { 'nav-mobile--scroll-locked': isNavMobileLocaleSwitcherOpen },
          { 'nav-mobile--with-footer': buttonNavigation.length },
        ]"
        :style="{ '--footer-size': buttonNavigation.length || undefined }"
      >
        <div class="nav-mobile__header">
          <T3Link
            :to="homeLink"
            :aria-label="translation.goHome"
            class="nav-mobile__logo"
          >
            <span @click="closeNavMobile">
              <Logo />
            </span>
          </T3Link>

          <label for="navMobileToggle">
            <NavToggle close />
          </label>
        </div>

        <div
          ref="navMobile"
          class="nav-mobile__scrollable"
        >
          <ul
            v-if="navigation.length || minorNavigation.length"
            class="nav-mobile__list"
          >
            <li
              v-for="(link, index) in [...navigation, ...minorNavigation]"
              :key="link.title"
            >
              <NavMobileLink
                :link="link"
                :index="index"
                :open="openSubdropIndexes.includes(index)"
                :locale="props.locale"
                @link-click="closeNavMobile"
                @toggle-subdrop="handleNavMobileLinkSubdropToggle"
              />
            </li>
          </ul>

          <div
            v-if="socialNavigation.length"
            class="nav-mobile__social"
          >
            <p>{{ translation.socialListHeader }}</p>

            <ul class="nav-mobile__social-list">
              <li
                v-for="link in socialNavigation"
                :key="link.title"
              >
                <NavSocialLink
                  :link="link"
                  @click="closeNavMobile"
                />
              </li>
            </ul>
          </div>

          <NavMobileLocaleSwitcher
            :locale-navigation="localeNavigation"
            :open="isNavMobileLocaleSwitcherOpen"
            :translation="translation"
            @link-click="closeNavMobile"
            @toggle="handleNavMobileLocaleSwitcherToogle"
          />
        </div>

        <ul
          v-if="buttonNavigation.length"
          ref="navMobileFooter"
          class="nav-mobile__footer"
        >
          <li
            v-for="button in buttonNavigation"
            :key="button.content"
            @click="closeNavMobile"
          >
            <ButtonBase :button="button" />
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
import { clearAllBodyScrollLocks, toggleBodyScrollLock } from 'body-scroll-lock-enhanced'
import { onUnmounted, ref, watch } from 'vue'

import type { ILocaleNavigation, INavigation, ISocialNavigation, UiButtonProps } from '~ui/types'

import NavSocialLink from '../NavComponents/NavSocialLink.vue'
import NavToggle from '../NavComponents/NavToggle.vue'

const navMobile = ref<HTMLElement | null>(null)
const isNavMobileOpen = ref<boolean>(false)
const isNavMobileLocaleSwitcherOpen = ref<boolean>(false)
const openSubdropIndexes = ref<Array<number>>([])

interface Props {
  navigation: Array<INavigation>
  minorNavigation: Array<INavigation>
  buttonNavigation: Array<UiButtonProps>
  localeNavigation: Array<ILocaleNavigation>
  socialNavigation: Array<ISocialNavigation>
  homeLink: string
  locale: 'en' | 'pl' | 'de'
  translation: Record<string, string>
}

const props = defineProps<Props>()

const closeNavMobile = () => {
  isNavMobileOpen.value = false
  isNavMobileLocaleSwitcherOpen.value = false
  openSubdropIndexes.value = []

  navMobile.value?.scrollTo({ top: 0, behavior: 'instant' })
}

const handleNavMobileLocaleSwitcherToogle = (value: boolean) => {
  isNavMobileLocaleSwitcherOpen.value = value
}

const handleNavMobileLinkSubdropToggle = (id: unknown) => {
  if (typeof id !== 'number') {
    openSubdropIndexes.value = []
  } else {
    openSubdropIndexes.value = openSubdropIndexes.value.includes(id)
      ? openSubdropIndexes.value.filter((linkId) => linkId !== id)
      : [...openSubdropIndexes.value, id]
  }
}

onUnmounted(clearAllBodyScrollLocks)

watch(isNavMobileOpen, (value) => {
  document.body.classList.toggle('nav-mobile-open', value)
  toggleBodyScrollLock(navMobile.value!, { toggleValue: value })
})
</script>

<style lang="scss">
.nav-mobile {
  &-container > * {
    position: relative;
    z-index: z-index(nav - 1);
    color: color(grey-850);

    & > svg path {
      fill: currentColor;
    }

    &#navMobileToggle {
      display: none;
    }
  }

  &-wrapper {
    position: fixed;
    inset: 0;
    z-index: z-index(nav);
    overflow: hidden;
    height: 100vh;
    height: 100dvh;
    width: 100%;
    max-width: 100vw;
    pointer-events: none;
  }

  position: absolute;
  inset: 0;
  bottom: unset;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateX(100%);
  transition:
    opacity 0.4s,
    visibility 0.4s,
    transform 0.3s ease;

  #navMobileToggle:checked ~ &-wrapper & {
    opacity: 1;
    visibility: visible;
    pointer-events: all;
    transform: translateX(0);
  }

  &__header {
    height: var(--subdrop-offset);
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: rem(8px);
    padding-inline: rem($container-padding);
    background-color: color(tarawera);

    & > label svg path {
      fill: color(white);
    }
  }

  &__logo:is(a) {
    color: color(white);
  }

  :where(& ul) {
    margin: 0;
  }

  &__scrollable {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: rem(32px);
    height: 100%;
    overflow-y: auto;
    color: color(grey-850);
    background-color: color(white);
    overscroll-behavior: none;
    scrollbar-width: none;

    & ::-webkit-scrollbar {
      display: none;
    }
  }

  &--scroll-locked &__scrollable {
    overflow-y: hidden;
  }

  &__list > * {
    border-bottom: 1px solid color(grey-250);
  }

  &__social {
    color: color(grey-800);
    padding-inline: rem($container-padding);

    & > p {
      font-weight: 500;
      margin: 0;
    }
  }

  &__social-list {
    display: flex;
    gap: rem(8px);
    overflow: auto;
    padding-block: rem(12px);
    margin-bottom: rem(2px);
    overscroll-behavior: none;
    scrollbar-width: none;

    & ::-webkit-scrollbar {
      display: none;
    }
  }

  & .nav-mobile-locale-switcher {
    margin-top: auto;
  }

  $button-height: rem(56px);

  &__footer {
    display: flex;
    flex-direction: column;
    gap: rem($container-padding);
    margin-top: auto;
    padding: rem($container-padding);
    background-color: color(white);
    border-top: 1px solid color(grey-250);

    & .button-base {
      height: $button-height;
      width: 100%;
      margin: 0;

      & > span {
        height: 100%;
      }
    }
  }

  &--with-footer {
    --overlay-position: calc(
      #{rem($container-padding)} * (var(--footer-size) + 1) + #{$button-height} *
        var(--footer-size)
    );
  }

  &__header,
  &__footer {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
  }

  .header--themed &-container {
    color: color(white);
  }
}
</style>
